import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, XCircle } from 'lucide-react';
import { Button } from '../../Button';
import { Modal } from '../../common/Modal';
import { NativeSelect } from '../../common/NativeSelect';
import { useApplicationReview } from '../../../contexts/ApplicationReviewContext';
import { rejectionReasons } from '../../../data/applicationReview';
import { can } from '../../../utils/applicationQueue';
import type { OwnerApplication } from '../../../types/owner';
export function RejectApplicationModal({
  app,
  onClose
}: {
  app: OwnerApplication | null;
  onClose: () => void;
}) {
  const {
    reject,
    role,
    user
  } = useApplicationReview();
  const [reason, setReason] = useState(rejectionReasons[0]);
  const [detail, setDetail] = useState('');
  const [reference, setReference] = useState('');
  const [confirm, setConfirm] = useState(false);
  useEffect(() => {
    if (app) {
      setReason(rejectionReasons[0]);
      setDetail('');
      setReference('');
      setConfirm(false);
    }
  }, [app?.id]);
  const allowed = can(role, 'reject');
  const valid = detail.trim().length > 4;
  const doReject = () => {
    if (!app) return;
    reject(app, {
      reason,
      detail,
      reference
    });
    toast.error(`${app.id} বাতিল করা হয়েছে — কোনো QR স্টিকার ইস্যু হবে না`);
    onClose();
  };
  return <>
      <Modal open={Boolean(app) && !confirm} onClose={onClose} title="আবেদন বাতিল" footer={<>
            <Button variant="outline" onClick={onClose} data-id="element-485" data-mp="3u0ulffynd87o">ফিরে যান</Button>
            <Button variant="destructive" disabled={!valid || !allowed} onClick={() => setConfirm(true)} data-id="element-486" data-mp="3p1retr30qtxl"><XCircle className="h-4 w-4" data-id="element-487" data-mp="3k2yaao5pmd9k" />বাতিল নিশ্চিত করুন</Button>
          </>} data-id="element-484" data-mp="31am0xhgn2bpx">
        {app && <div className="space-y-4" data-id="element-488" data-mp="2dnduuxbdyymu">
            <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600" data-id="element-489" data-mp="2dofj63hexhmi"><b className="text-navy-900" data-id="element-490" data-mp="28ni0lpshqlkh">{app.id}</b> • {app.vehicleReg} • {app.ownerName}</p>
            {!allowed && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700" data-id="element-491" data-mp="10pdmto6xmv7c">কেবল অনুমোদনকারী কর্মকর্তা আবেদন বাতিল করতে পারেন। উপরে ভূমিকা পরিবর্তন করুন।</p>}
            <NativeSelect id="rj-reason" label="বাতিলের কারণ *" options={rejectionReasons} value={reason} onChange={e => setReason(e.target.value)} data-id="element-492" data-mp="qq4vl33gbuoo" />
            <div data-id="element-493" data-mp="vnxztql72emb">
              <label htmlFor="rj-detail" className="mb-1 block text-xs font-medium text-slate-600" data-id="element-494" data-mp="vnelejur68d1">বিস্তারিত মন্তব্য <span className="text-red-600" data-id="element-495" data-mp="gy8lsus06g4m">*</span></label>
              <textarea id="rj-detail" rows={3} value={detail} onChange={e => setDetail(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100" data-id="element-496" data-mp="ls3e2mloc3fb" />
            </div>
            <div data-id="element-497" data-mp="3u6v69v9zavwp">
              <label htmlFor="rj-ref" className="mb-1 block text-xs font-medium text-slate-600" data-id="element-498" data-mp="3u6d46i0mxngv">সহায়ক সূত্র (ঐচ্ছিক)</label>
              <input id="rj-ref" value={reference} onChange={e => setReference(e.target.value)} placeholder="যেমন: BRTA পত্র নং / মামলা নম্বর" className="h-9 w-full rounded-md border border-input px-3 text-sm outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100" data-id="element-499" data-mp="2z8xjo8ek38z" />
            </div>
          </div>}
      </Modal>
      <Modal open={Boolean(app) && confirm} onClose={() => setConfirm(false)} title="বাতিল নিশ্চিতকরণ" footer={<>
            <Button variant="outline" onClick={() => setConfirm(false)} data-id="element-501" data-mp="2cbledqk4it9">না, ফিরে যান</Button>
            <Button variant="destructive" onClick={doReject} data-id="element-502" data-mp="3ugduv4u9a4av">হ্যাঁ, বাতিল করুন</Button>
          </>} data-id="element-500" data-mp="3pg9dlclix5jl">
        <div className="flex gap-3" data-id="element-503" data-mp="c2xtznzuw92g">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 text-red-600" data-id="element-504" data-mp="74cszmcs5lcy"><AlertTriangle className="h-5 w-5" data-id="element-505" data-mp="m1n42uligp85" /></span>
          <div className="text-sm text-slate-700" data-id="element-506" data-mp="m256k8yrnzoi">
            <p className="font-semibold text-navy-900" data-id="element-507" data-mp="h2smtx36z8q5">আপনি কি নিশ্চিত যে আবেদনটি বাতিল করতে চান?</p>
            <p className="mt-1" data-id="element-508" data-mp="2alj2gf70j5n7">কারণ: {reason}</p>
            <p className="mt-1 text-xs text-slate-500" data-id="element-509" data-mp="2akq4o3q17qh7">বাতিলকারী: {user.user} ({user.role}) • মালিককে নোটিফিকেশন পাঠানো হবে এবং আবেদনের ইতিহাস সংরক্ষিত থাকবে।</p>
          </div>
        </div>
      </Modal>
    </>;
}